'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

interface IconItem {
  name: string;
  path: string;
  src: string;
}

interface IconContextType {
  iconList: IconItem[];
  loading: boolean;
  hasLoaded: boolean;
  loadIcons: () => Promise<void>;
  refreshIcons: () => Promise<void>;
}

const IconContext = createContext<IconContextType | undefined>(undefined);

export const useIcon = () => {
  const context = useContext(IconContext);
  if (!context) {
    throw new Error('useIcon 必须在 IconProvider 内部使用');
  }
  return context;
};

interface IconProviderProps {
  children: React.ReactNode;
}

export const IconProvider: React.FC<IconProviderProps> = ({ children }) => {
  const [iconList, setIconList] = useState<IconItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadIcons = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await fetch('/api/svg-files');
      if (!response.ok) {
        throw new Error(`HTTP 错误! 状态: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.files) {
        const icons = result.data.files.map((file: any) => ({
          name: file.name,
          path: file.path,
          src: file.path, // 使用相对路径作为src
        }));
        setIconList(icons);
        setHasLoaded(true);
        console.log('成功加载图标:', icons.length, '个');
      } else {
        throw new Error(result.message || '获取图标数据失败');
      }
    } catch (error) {
      console.error('加载图标失败:', error);
      setIconList([]);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const refreshIcons = useCallback(async () => {
    setHasLoaded(false);
    await loadIcons();
  }, [loadIcons]);

  const value: IconContextType = {
    iconList,
    loading,
    hasLoaded,
    loadIcons,
    refreshIcons,
  };

  return <IconContext.Provider value={value}>{children}</IconContext.Provider>;
};

export default IconProvider;
